/**
 * MBA Lab — the ONLY module in the time-zone planner that touches `Intl`.
 *
 * lib/timezone-model.js is deliberately pure (no Intl, no DOM, no clock) and
 * takes precomputed offsets. This adapter is the seam that produces them, and
 * it is kept deliberately thin so the untestable surface stays small:
 * everything here is either an offset lookup or a zone list.
 *
 * `resolveOffsetMinutes` takes an injectable `dtfFactory` so the seam itself
 * is testable without depending on the host engine's tz database - a fake
 * factory returning fixed parts proves the parts-rebuild arithmetic, and the
 * pure model's own tests then feed it literal offsets.
 */

/**
 * Curated fallback for engines without `Intl.supportedValuesOf` (Safari
 * shipped it late; some embedded WebViews still lack it). Not a replacement
 * for the real tz database - just enough spread that a student in any major
 * cohort city can find themselves, with the offset range covered end to end.
 */
const FALLBACK_ZONES = Object.freeze([
  'Pacific/Midway',
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Vancouver',
  'America/Phoenix',
  'America/Denver',
  'America/Mexico_City',
  'America/Chicago',
  'America/Bogota',
  'America/Lima',
  'America/New_York',
  'America/Toronto',
  'America/Santiago',
  'America/Halifax',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'Atlantic/Azores',
  'UTC',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Africa/Lagos',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Zurich',
  'Europe/Warsaw',
  'Africa/Johannesburg',
  'Africa/Cairo',
  'Europe/Athens',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Moscow',
  'Asia/Jerusalem',
  'Asia/Riyadh',
  'Asia/Tehran',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Colombo',
  'Asia/Kathmandu',
  'Asia/Dhaka',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Manila',
  'Australia/Perth',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
  'Pacific/Fiji',
]);

const REQUIRED_PARTS = ['year', 'month', 'day', 'hour', 'minute', 'second'];

function partsToNumbers(parts) {
  const out = {};
  for (const part of parts || []) {
    if (!part || typeof part.type !== 'string') continue;
    if (REQUIRED_PARTS.indexOf(part.type) === -1) continue;
    const value = Number(part.value);
    if (!Number.isFinite(value)) return null;
    out[part.type] = value;
  }
  for (const key of REQUIRED_PARTS) {
    if (!Number.isFinite(out[key])) return null;
  }
  return out;
}

/**
 * The UTC offset a zone is on at a given instant, in minutes
 * (local = UTC + offsetMinutes). New York in January is -300; Kolkata is
 * +330; Kathmandu is +345.
 *
 * Works by formatting the instant IN the target zone, then rebuilding those
 * wall-clock parts as if they were UTC and measuring the gap. That is the
 * only offset mechanism that is correct for every zone in every engine -
 * `Date.prototype.getTimezoneOffset` only ever reports the host's own zone.
 *
 * NEVER THROWS. An unknown or misspelled zone (including one restored from
 * storage after a tz-database rename) returns null, and the model excludes
 * that member from the math rather than guessing.
 *
 * @param {string} zone IANA zone id
 * @param {number} refInstantMs epoch milliseconds
 * @param {Function} [dtfFactory] injected for tests; defaults to Intl.DateTimeFormat
 * @returns {number|null} offset in minutes, or null if the zone cannot be resolved
 */
export function resolveOffsetMinutes(zone, refInstantMs, dtfFactory) {
  if (typeof zone !== 'string' || !zone.trim()) return null;
  if (!Number.isFinite(refInstantMs)) return null;

  const Factory = dtfFactory
    || (typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat : null);
  if (typeof Factory !== 'function') return null;

  try {
    const formatter = new Factory('en-US', {
      timeZone: zone.trim(),
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    if (!formatter || typeof formatter.formatToParts !== 'function') return null;

    const numbers = partsToNumbers(formatter.formatToParts(new Date(refInstantMs)));
    if (!numbers) return null;

    // hour12:false yields "24" for midnight in some engines (a long-standing
    // ICU quirk). Left unhandled it shifts the offset by a full day.
    const hour = numbers.hour === 24 ? 0 : numbers.hour;

    const asIfUtc = Date.UTC(numbers.year, numbers.month - 1, numbers.day, hour, numbers.minute, numbers.second);
    if (!Number.isFinite(asIfUtc)) return null;

    // Drop sub-second precision on both sides so the difference is a whole
    // number of minutes rather than a fraction that rounds unpredictably.
    const refWholeSeconds = Math.floor(refInstantMs / 1000) * 1000;
    const offset = Math.round((asIfUtc - refWholeSeconds) / 60000);

    // Real offsets live in [-12:00, +14:00]. Anything outside that came from
    // a malformed parts payload, not from a time zone.
    if (offset < -720 || offset > 840) return null;
    return offset;
  } catch {
    return null;
  }
}

/**
 * Every IANA zone the engine knows, or the curated fallback above.
 * @returns {string[]}
 */
export function listZones() {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      const zones = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(zones) && zones.length) return zones.slice();
    }
  } catch {
    // Fall through to the curated list.
  }
  return FALLBACK_ZONES.slice();
}

/** "America/New_York" -> "New York (America/New_York)". */
export function zoneLabel(zone) {
  if (typeof zone !== 'string' || !zone) return '';
  const segments = zone.split('/');
  const city = segments[segments.length - 1].replace(/_/g, ' ');
  return segments.length > 1 ? `${city} (${zone})` : zone;
}

/** Datalist rows, sorted by the city a person would actually type. */
export function zoneChoices() {
  return listZones()
    .map((zone) => ({ zone, label: zoneLabel(zone) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The visitor's own zone, for the "Use my time zone" button. Null when the
 * engine will not tell us - the picker still works by hand.
 */
export function localZone() {
  try {
    if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return null;
    const resolved = new Intl.DateTimeFormat().resolvedOptions();
    return resolved && typeof resolved.timeZone === 'string' && resolved.timeZone ? resolved.timeZone : null;
  } catch {
    return null;
  }
}

/**
 * Accepts either a raw IANA id or a datalist label ("Berlin
 * (Europe/Berlin)") and returns the id. An unrecognised value is returned
 * as typed rather than rejected - resolveOffsetMinutes will return null for
 * it and the UI flags that member for a re-pick, which is a better failure
 * than silently dropping what somebody typed.
 */
export function parseZoneInput(value, knownZones) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  const bracketed = /\(([^)]+)\)\s*$/.exec(trimmed);
  const candidate = bracketed ? bracketed[1].trim() : trimmed;

  const known = knownZones instanceof Set
    ? knownZones
    : new Set(Array.isArray(knownZones) ? knownZones : listZones());
  if (known.has(candidate)) return candidate;

  const lowered = candidate.toLowerCase();
  for (const zone of known) {
    if (zone.toLowerCase() === lowered) return zone;
  }
  return candidate;
}

/**
 * Resolves one instant's offsets for a whole member list. Returns model-shaped
 * members ({ ...member, offsetMinutes }) - offsetMinutes is null for anything
 * that will not resolve, which is exactly what the model expects.
 */
export function withOffsets(members, refInstantMs, dtfFactory) {
  return (Array.isArray(members) ? members : []).map((m) => ({
    ...m,
    offsetMinutes: resolveOffsetMinutes(m ? m.zone : '', refInstantMs, dtfFactory),
  }));
}
